import React from "react";
import { Link, useHistory } from "react-router-dom";
import AcademyCourseCategories from "src/academy/course/AcademyCourseCategories/AcademyCourseCategories";
import AcademyImage from "src/academy/course/AcademyImage/AcademyImage";
import AcademyCourseMetaDataRow from "src/academy/course/AcademyCourseMetaDataRow/AcademyCourseMetaDataRow";
import AcademyCourseProgress from "src/academy/course/AcademyCourseProgress/AcademyCourseProgress";
import { IAcademyCourse } from "src/academy/types";
import { Label } from "src/ui";
import "./AcademyCourseListItem.scss";
import StandardActionMenu from "src/ui/components/ActionMenu/StandardActionMenu";
import { useSelector } from "react-redux";
import api from "src/api";
import classNames from "classnames";
import { localize } from "src/l10n";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { Stack } from "@fluentui/react";
import { truncate } from "src/utils";
import dompurify from "dompurify";

interface IProps {
    course: IAcademyCourse;
    big?: boolean;
    locked?: boolean;
    hideActionMenu?: boolean;
    isCoursePackage?: boolean;
    coursePackageId? :number;
    onPinToggle?: () => void;
}

// NOTE: Also used in feed for shared courses
const AcademyCourseListItem = (props: IProps) => {
    const isAdminOrEditor = useSelector<Spintr.AppState, boolean>(
        (appState) => appState.profile.active.isAdmin || appState.profile.active.isEditor
    );

    const history = useHistory();
    const sanitizer = dompurify.sanitize;

    const content = (
        <>
            <div className="AcademyCourseListItem-image">
                <AcademyImage imageUrl={props.course.imageUrl} />
                {!props.hideActionMenu && (
                    <StandardActionMenu
                        canShare={!props.isCoursePackage}
                        canEdit={isAdminOrEditor && !props.isCoursePackage}
                        canPin={isAdminOrEditor && !props.isCoursePackage}
                        onEditClick={() => {
                            history.push(
                                `/admin/learning/${props.isCoursePackage ? "packages/" : ""}${props.course.id}/edit`
                            );
                        }}
                        onPinClick={async () => {
                            await api.post("/api/v1/objects/toggle-pin/" + props.course.id);
                            props.onPinToggle?.();
                        }}
                        isPinned={props.course.isPinned}
                        objectId={props.course.id}
                    />
                )}
            </div>
            <div className="AcademyCourseListItem-info">
                <div>
                    <Label size="h3" className="course-name" weight="semi-bold" color="dark-grey">{props.course.name}</Label>
                    {props.course.category && <AcademyCourseCategories categories={[props.course.category]}  />}
                    {props.course.description && (
                        <Label
                            className="course-description"
                            size="body-1"
                            color="dark-grey"
                            dangerouslySetInnerHTML={{
                                __html: truncate(sanitizer(props.course.description, { ALLOWED_TAGS: [] }), 250),
                            }}
                        />
                    )}
                </div>
                <div>
                    <AcademyCourseMetaDataRow
                        lessonCount={props.course.statistics.lessonCount}
                        userCount={props.course.statistics.usersTotal}
                        language={props.course.language}
                        languages={props.course.languages}
                        isCoursePackage={props.isCoursePackage}
                    />
                    {props.course.userProgress.isInitialized && (
                        <AcademyCourseProgress userProgress={props.course.userProgress} />
                    )}
                </div>
            </div>
            {props.locked && (
                <div className="locked">
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }} horizontalAlign="center">
                        <Visage2Icon icon="lock" />
                        <Label size="body-2">{localize("COMPLETE_PREVIOUS_COURSE_TO_UNLOCK")}</Label>
                    </Stack>
                </div>
            )}
        </>
    );

    const className = classNames("AcademyCourseListItem", { big: props.big, locked: props.locked });

    return props.locked ? (
        <div className={className}>{content}</div>
    ) : (
        <Link
            to={
                "/learning/" +
                (props.isCoursePackage ? "packages/" : "") +
                (props.coursePackageId ? "packages/" + props.coursePackageId + "/" : "") +
                props.course.id
            }
            className={className}
        >
            {content}
        </Link>
    );
};

export default AcademyCourseListItem;
